import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Application } from '@feathersjs/feathers';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Pagination,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import superagent from 'superagent';
import styled from 'styled-components';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import {
  ContestShapefile,
  ContestShapefileRow,
  getContestShapefileErrorMessage,
  PatchContestShapefile
} from '../../app/feathers/contest-shapefiles';
import { DataOperation } from '../../app/feathers/candidate-lists/CandidateList';
import { DataOperation as StoredDataOperation } from '../../app/feathers/data-operations';
import { District } from '../../app/feathers/districts/District';
import { Election } from '../../app/feathers/elections/Election';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import {
  DataDetailToolbar,
  DistrictCell,
  DistrictSelectInput,
  InteractiveMap,
  LoadingSpinner,
  ProgressBar,
  StepTracker
} from '../../components';
import DataOperationsTable from '../UploadCandidateList/DataOperationsTable';
import { useFeathers } from '../../app/util';
import {
  approveContestShapefileParentBoundary,
  assignContestShapefileRowDistrict,
  clearContestShapefileRowDistrict,
  confirmContestShapefileRowMatch,
  contestShapefileRowAttentionMessage,
  contestShapefileRowNeedsAttention,
  contestShapefileRowParentDistrictId,
  isContestShapefileActive,
  replaceContestShapefileRow,
  setContestShapefileRowIncluded,
  validateContestShapefileRows
} from './workflow';
import { formatGeoJSON } from '../../app/util/geo-projection-helpers';

const ROW_PAGE_SIZE = 50;
const VALIDATION_PAGE_SIZE = 500;

type RootState = {
  elections?: { byKey: Record<string, Election | undefined> };
};

const errorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Something went wrong. Please try again.';
};

const isAppendBoundaryRow = (row?: ContestShapefileRow | null): boolean => Boolean(
  row?.boundaryAction === 'append'
  || (
    row?.match?.district?.hasGeodistrict === false
    && row?.match?.district?.hasPartialGeodistrict === true
  )
);

const hasParentBoundaryWarning = (row?: ContestShapefileRow | null): boolean => (
  row?.parentBoundaryComparison?.insufficientContainment === true
  && row.parentBoundaryComparison.manuallyApproved !== true
);

const pageFromSearch = (search: string): number => {
  const page = Number(new URLSearchParams(search).get('page'));
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const ContestShapefileDetail: React.FC = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const { key: electionKey, id, stepName } = useParams<{
    key: string;
    id: string;
    stepName?: string;
  }>();
  const history = useHistory();
  const location = useLocation();
  const preloadedElection = useSelector((state: RootState) => state.elections?.byKey[electionKey]);
  const mode = id === 'new' ? 'new' : 'existing';
  const [election, setElection] = useState<Election | null>(preloadedElection || null);
  const [stateConfiguration, setStateConfiguration] = useState<StateConfiguration | null>(null);
  const [job, setJob] = useState<ContestShapefile | null>(null);
  const [dataOperations, setDataOperations] = useState<StoredDataOperation[]>([]);
  const [county, setCounty] = useState<District | null>(null);
  const [rows, setRows] = useState<ContestShapefileRow[]>([]);
  const [validationRows, setValidationRows] = useState<ContestShapefileRow[]>([]);
  const [rowPage, setRowPage] = useState(() => pageFromSearch(location.search));
  const [rowTotal, setRowTotal] = useState(0);
  const [selectedCounty, setSelectedCounty] = useState<District | null>(null);
  const [loading, setLoading] = useState(mode === 'existing');
  const [uploading, setUploading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [creationDialogOpen, setCreationDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [attentionFilter, setAttentionFilter] = useState<boolean | null>(null);
  const [reviewSearch, setReviewSearch] = useState('');
  const [mapRow, setMapRow] = useState<ContestShapefileRow | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [comparisonMap, setComparisonMap] = useState<{
    row: ContestShapefileRow;
    incomingBoundary?: unknown;
    existingBoundary?: unknown;
    countyBoundary?: unknown;
    countyName?: string;
    comparisonBoundaryName?: string;
  } | null>(null);
  const [comparisonMapLoading, setComparisonMapLoading] = useState(false);
  const [comparisonMapError, setComparisonMapError] = useState<string | null>(null);
  const [s3LinkCopied, setS3LinkCopied] = useState(false);
  const [rowGeometries, setRowGeometries] = useState<Record<number, any>>({});
  const [parentDistricts, setParentDistricts] = useState<Record<string, District>>({});
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const reviewSearchInput = useRef<HTMLInputElement>(null);
  const rowGeometriesRef = useRef<Record<number, any>>({});
  const geometryRequestsRef = useRef<Map<number, Promise<any>>>(new Map());
  const parentDistrictsRef = useRef<Record<string, District>>({});
  const parentDistrictRequestsRef = useRef<Map<string, Promise<District | undefined>>>(new Map());
  const geometryJobIdRef = useRef<string | null>(null);
  const comparisonMapRequestRef = useRef(0);
  const loadedRowPageRef = useRef<string | null>(null);
  const creationHasStarted = Boolean(
    job?.creationStartedAt
    || dataOperations.length
    || job?.status === 'creating'
    || job?.status === 'completed'
  );
  const showVerifiedStep = !creationHasStarted || stepName === 'verified';
  const showCreatedStep = creationHasStarted && !showVerifiedStep;

  const changeRowPage = useCallback((page: number) => {
    setRowPage(page);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', `${page}`);
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
      hash: location.hash
    });
  }, [history, location.hash, location.pathname, location.search]);

  useEffect(() => {
    setRowPage(pageFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    if (job?.status !== 'needs-review') return;
    const focusReviewSearch = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'f') return;
      event.preventDefault();
      reviewSearchInput.current?.focus();
      reviewSearchInput.current?.select();
    };
    window.addEventListener('keydown', focusReviewSearch);
    return () => window.removeEventListener('keydown', focusReviewSearch);
  }, [job?.status]);

  const loadCompact = useCallback(async () => {
    if (!feathers || mode === 'new') return null;
    const next = await feathers.getService('contest-shapefiles').get(id, {
      query: {
        $includeRows: false,
        $includeGeometry: false
      }
    });
    setJob(next);
    return next;
  }, [feathers, id, mode]);

  const loadDataOperations = useCallback(async () => {
    if (!feathers || mode === 'new') return [];
    const pageSize = 50;
    const operations: StoredDataOperation[] = [];
    let skip = 0;
    let total = 0;
    do {
      const result = await feathers.getService('data-operations').find({
        query: {
          operationRunner: 'contest-shapefiles',
          operationRunnerId: id,
          $sort: { createdAt: 1 },
          $limit: pageSize,
          $skip: skip
        }
      });
      operations.push(...result.data);
      total = result.total;
      if (result.data.length === 0) break;
      skip += result.data.length;
    } while (skip < total);
    setDataOperations(operations);
    return operations;
  }, [feathers, id, mode]);

  const loadRows = useCallback(async (
    record: ContestShapefile,
    page: number,
    includeValidation = false
  ) => {
    const pageResult = await feathers.getService('contest-shapefiles').get(record._id, {
      query: {
        $includeRows: true,
        $includeGeometry: false,
        $rowSkip: (page - 1) * ROW_PAGE_SIZE,
        $rowLimit: ROW_PAGE_SIZE
      }
    });
    setRows(pageResult.rows || []);
    const total = pageResult.rowTotal ?? record.totalRows ?? 0;
    setRowTotal(total);

    if (!includeValidation) {
      setValidationRows([]);
      return;
    }

    const allRows: ContestShapefileRow[] = [];
    for (let skip = 0; skip < total; skip += VALIDATION_PAGE_SIZE) {
      const result = await feathers.getService('contest-shapefiles').get(record._id, {
        query: {
          $includeRows: true,
          $includeGeometry: false,
          $rowSkip: skip,
          $rowLimit: VALIDATION_PAGE_SIZE
        }
      });
      allRows.push(...(result.rows || []));
    }
    setValidationRows(allRows);
  }, [feathers]);

  useEffect(() => {
    if (!feathers) return;
    if (!preloadedElection) {
      feathers.getService('elections').get(electionKey)
        .then(setElection)
        .catch(error => setError(errorMessage(error)));
    }
    if (mode === 'existing') {
      setLoading(true);
      Promise.all([loadCompact(), loadDataOperations()])
        .catch(error => setError(errorMessage(error)))
        .finally(() => setLoading(false));
    }
  }, [electionKey, feathers, loadCompact, loadDataOperations, mode, preloadedElection]);

  useEffect(() => {
    if (mode !== 'existing' || !job) return;
    const basePath = `/elections/${electionKey}/contest-shapefiles/${id}`;
    if (creationHasStarted) {
      const normalizedStep = stepName === 'verified' ? 'verified' : 'created';
      if (stepName !== normalizedStep) {
        history.replace(`${basePath}/${normalizedStep}${location.search}`);
      }
      return;
    }
    if (job.status === 'needs-review' && stepName !== 'verified') {
      history.replace(`${basePath}/verified${location.search}`);
    } else if (job.status !== 'needs-review' && stepName) {
      history.replace(`${basePath}${location.search}`);
    }
  }, [
    creationHasStarted,
    electionKey,
    history,
    id,
    job,
    location.search,
    mode,
    stepName
  ]);

  useEffect(() => {
    if (!feathers || !election?.stateCode) return;
    feathers.getService('state-configurations')
      .get(election.stateCode.toLowerCase())
      .then(setStateConfiguration)
      .catch(() => setStateConfiguration(null));
  }, [election?.stateCode, feathers]);

  useEffect(() => {
    if (!feathers || mode !== 'existing' || !job?.county) return;
    let current = true;
    feathers.getService('districts').find({
      query: {
        _id: job.county,
        $limit: 1
      }
    })
      .then((response) => {
        if (current) setCounty(response.data[0] || null);
      })
      .catch(() => {
        if (current) setCounty(null);
      });
    return () => {
      current = false;
    };
  }, [feathers, job?.county, mode]);

  useEffect(() => {
    if (!job || showCreatedStep) return;
    const needsValidation = job.status === 'needs-review' || showVerifiedStep;
    const rowPageKey = `${job._id}:${rowPage}:${needsValidation}`;
    const showBlockingLoader = needsValidation && loadedRowPageRef.current !== rowPageKey;
    if (showBlockingLoader) setLoading(true);
    loadRows(job, rowPage, needsValidation)
      .catch(error => setError(errorMessage(error)))
      .finally(() => {
        loadedRowPageRef.current = rowPageKey;
        if (showBlockingLoader) setLoading(false);
      });
  }, [
    job,
    loadRows,
    rowPage,
    showCreatedStep,
    showVerifiedStep
  ]);

  useEffect(() => {
    if (!isContestShapefileActive(job?.status)) return;
    const interval = window.setInterval(() => {
      Promise.all([loadCompact(), loadDataOperations()])
        .catch(error => setError(errorMessage(error)));
    }, 3000);
    return () => window.clearInterval(interval);
  }, [job?.status, loadCompact, loadDataOperations]);

  useEffect(() => {
    if (!s3LinkCopied) return;
    const timeout = window.setTimeout(() => setS3LinkCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [s3LinkCopied]);

  const validation = useMemo(
    () => validateContestShapefileRows(validationRows),
    [validationRows]
  );
  const attentionRows = useMemo(
    () => validationRows.filter(row => contestShapefileRowNeedsAttention(row)),
    [validationRows]
  );
  const reviewProgress = useMemo(() => {
    const includedRows = validationRows.filter(row => row.included);
    const completed = includedRows.filter(row => (
      !contestShapefileRowNeedsAttention(row)
    )).length;
    const total = includedRows.length;
    return {
      completed,
      total,
      percentage: total > 0 ? (completed / total) * 100 : 0
    };
  }, [validationRows]);
  const filteredReviewRows = useMemo(() => {
    const sourceRows = attentionFilter ? attentionRows : validationRows;
    const search = reviewSearch.trim().toLocaleLowerCase();
    if (!search) return sourceRows;
    return sourceRows.filter((row) => {
      const district = row.match?.district;
      return [
        row.contest,
        district?.name,
        district?.longName,
        district?.shortName,
        district?.matchName
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase()
        .includes(search);
    });
  }, [attentionFilter, attentionRows, reviewSearch, validationRows]);
  const visibleRows = showVerifiedStep
    ? filteredReviewRows.slice((rowPage - 1) * ROW_PAGE_SIZE, rowPage * ROW_PAGE_SIZE)
    : rows;
  const visibleRowTotal = showVerifiedStep
    ? filteredReviewRows.length
    : rowTotal;
  const visibleRowDataIndexesKey = visibleRows.map(row => row.dataIndex).join(',');
  const visibleParentDistrictIdsKey = Array.from(new Set(visibleRows.flatMap((row) => {
    const parentId = contestShapefileRowParentDistrictId(row, election?.state);
    return parentId
      ? [parentId]
      : [];
  }))).join(',');

  const patchRow = async (
    previousRow: ContestShapefileRow,
    patch: PatchContestShapefile,
    optimisticRow: ContestShapefileRow
  ) => {
    if (!job) return false;
    const dataIndex = previousRow.dataIndex;
    setSavingRows(current => new Set(current).add(dataIndex));
    setError(null);
    setRows(current => replaceContestShapefileRow(current, optimisticRow));
    setValidationRows(current => replaceContestShapefileRow(current, optimisticRow));
    try {
      const result = await feathers.getService('contest-shapefiles').patch(job._id, patch);
      const updatedRow = result.updatedRow;
      if (updatedRow) {
        setRows(current => replaceContestShapefileRow(current, updatedRow));
        setValidationRows(current => replaceContestShapefileRow(current, updatedRow));
      }
      return true;
    } catch (requestError) {
      setRows(current => replaceContestShapefileRow(current, previousRow));
      setValidationRows(current => replaceContestShapefileRow(current, previousRow));
      setError(errorMessage(requestError));
      return false;
    } finally {
      setSavingRows(current => {
        const next = new Set(current);
        next.delete(dataIndex);
        return next;
      });
    }
  };

  useEffect(() => {
    const jobId = job?._id || null;
    if (geometryJobIdRef.current === jobId) return;
    geometryJobIdRef.current = jobId;
    geometryRequestsRef.current.clear();
    parentDistrictRequestsRef.current.clear();
    rowGeometriesRef.current = {};
    parentDistrictsRef.current = {};
    setRowGeometries({});
    setParentDistricts({});
  }, [job?._id]);

  const requestRowGeometries = useCallback(async (dataIndexes: number[]) => {
    if (!job || dataIndexes.length === 0) return;
    const jobId = job._id;
    const result = await feathers.getService('contest-shapefiles').get(jobId, {
      query: {
        $includeRows: true,
        $includeGeometry: true,
        $rowDataIndexes: dataIndexes
      }
    });
    if (geometryJobIdRef.current !== jobId) return;

    const loadedGeometries = (result.rows || []).reduce((geometries, row) => {
      if (row.geometry !== undefined) geometries[row.dataIndex] = row.geometry;
      return geometries;
    }, {} as Record<number, any>);
    if (Object.keys(loadedGeometries).length === 0) return;

    rowGeometriesRef.current = {
      ...rowGeometriesRef.current,
      ...loadedGeometries
    };
    setRowGeometries(current => ({ ...current, ...loadedGeometries }));
  }, [feathers, job]);

  const loadRowGeometry = useCallback(async (dataIndex: number) => {
    if (Object.prototype.hasOwnProperty.call(rowGeometriesRef.current, dataIndex)) {
      return rowGeometriesRef.current[dataIndex];
    }

    const pendingRequest = geometryRequestsRef.current.get(dataIndex);
    if (pendingRequest) return pendingRequest;

    const request = requestRowGeometries([dataIndex])
      .then(() => rowGeometriesRef.current[dataIndex]);
    geometryRequestsRef.current.set(dataIndex, request);
    request.then(
      () => geometryRequestsRef.current.delete(dataIndex),
      () => geometryRequestsRef.current.delete(dataIndex)
    );
    return request;
  }, [requestRowGeometries]);

  const precacheRowGeometries = useCallback((dataIndexes: number[]) => {
    const missingIndexes = dataIndexes.filter(dataIndex => (
      !Object.prototype.hasOwnProperty.call(rowGeometriesRef.current, dataIndex)
      && !geometryRequestsRef.current.has(dataIndex)
    ));
    if (missingIndexes.length === 0) return;

    const batchRequest = requestRowGeometries(missingIndexes);
    missingIndexes.forEach((dataIndex) => {
      const rowRequest = batchRequest.then(() => rowGeometriesRef.current[dataIndex]);
      geometryRequestsRef.current.set(dataIndex, rowRequest);
      rowRequest.then(
        () => geometryRequestsRef.current.delete(dataIndex),
        () => geometryRequestsRef.current.delete(dataIndex)
      );
    });
    batchRequest.catch(() => {
      // Background preloading must not affect the page or its error state.
    });
  }, [requestRowGeometries]);

  const requestParentDistricts = useCallback(async (parentIds: string[]) => {
    if (!job || parentIds.length === 0) return;
    const jobId = job._id;
    const result = await feathers.getService('districts').find({
      query: {
        _id: { $in: parentIds },
        $includeGeoDistrict: true,
        $limit: parentIds.length
      }
    });
    if (geometryJobIdRef.current !== jobId) return;

    const loadedParents = result.data.reduce((districts, district) => {
      districts[district._id] = district;
      return districts;
    }, {} as Record<string, District>);
    if (Object.keys(loadedParents).length === 0) return;

    parentDistrictsRef.current = {
      ...parentDistrictsRef.current,
      ...loadedParents
    };
    setParentDistricts(current => ({ ...current, ...loadedParents }));
  }, [feathers, job]);

  const loadParentDistrict = useCallback(async (parentId: string) => {
    if (parentDistrictsRef.current[parentId]) {
      return parentDistrictsRef.current[parentId];
    }

    const pendingRequest = parentDistrictRequestsRef.current.get(parentId);
    if (pendingRequest) return pendingRequest;

    const request = requestParentDistricts([parentId])
      .then(() => parentDistrictsRef.current[parentId]);
    parentDistrictRequestsRef.current.set(parentId, request);
    request.then(
      () => parentDistrictRequestsRef.current.delete(parentId),
      () => parentDistrictRequestsRef.current.delete(parentId)
    );
    return request;
  }, [requestParentDistricts]);

  const precacheParentDistricts = useCallback((parentIds: string[]) => {
    const missingParentIds = parentIds.filter(parentId => (
      !parentDistrictsRef.current[parentId]
      && !parentDistrictRequestsRef.current.has(parentId)
    ));
    if (missingParentIds.length === 0) return;

    const batchRequest = requestParentDistricts(missingParentIds);
    missingParentIds.forEach((parentId) => {
      const parentRequest = batchRequest.then(() => parentDistrictsRef.current[parentId]);
      parentDistrictRequestsRef.current.set(parentId, parentRequest);
      parentRequest.then(
        () => parentDistrictRequestsRef.current.delete(parentId),
        () => parentDistrictRequestsRef.current.delete(parentId)
      );
    });
    batchRequest.catch(() => {
      // Background preloading must not affect the page or its error state.
    });
  }, [requestParentDistricts]);

  useEffect(() => {
    if (!visibleRowDataIndexesKey && !visibleParentDistrictIdsKey) return;
    const timeout = window.setTimeout(() => {
      if (visibleRowDataIndexesKey) {
        precacheRowGeometries(visibleRowDataIndexesKey.split(',').map(Number));
      }
      if (visibleParentDistrictIdsKey) {
        precacheParentDistricts(visibleParentDistrictIdsKey.split(','));
      }
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [
    precacheParentDistricts,
    precacheRowGeometries,
    visibleParentDistrictIdsKey,
    visibleRowDataIndexesKey
  ]);

  const openRowMap = async (row: ContestShapefileRow) => {
    if (!job) return;
    const hasCachedGeometry = Object.prototype.hasOwnProperty.call(
      rowGeometries,
      row.dataIndex
    );
    setMapRow(hasCachedGeometry
      ? { ...row, geometry: rowGeometries[row.dataIndex] }
      : row);
    setMapLoading(!hasCachedGeometry);
    setMapError(null);
    if (hasCachedGeometry) return;

    try {
      const geometry = await loadRowGeometry(row.dataIndex);
      if (!geometry) {
        throw new Error('Geometry is unavailable for this contest.');
      }
      setMapRow({ ...row, geometry });
    } catch (requestError) {
      setMapError(errorMessage(requestError));
    } finally {
      setMapLoading(false);
    }
  };

  const mapButton = (row: ContestShapefileRow) => (
    <Tooltip title={`View ${row.contest} boundary`}>
      <IconButton
        size="small"
        aria-label={`View ${row.contest} boundary`}
        onClick={() => openRowMap(row)}
      >
        <MapOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const closeComparisonMap = () => {
    comparisonMapRequestRef.current += 1;
    setComparisonMap(null);
    setComparisonMapError(null);
    setComparisonMapLoading(false);
  };

  const approveParentBoundaryWarning = async () => {
    const row = comparisonMap?.row;
    if (!row || !hasParentBoundaryWarning(row)) return;

    const approved = await patchRow(
      row,
      {
        $approveParentBoundaryComparison: {
          dataIndex: row.dataIndex
        }
      },
      approveContestShapefileParentBoundary(row)
    );
    if (approved) closeComparisonMap();
  };

  const openComparisonMap = async (row: ContestShapefileRow) => {
    const comparesParent = hasParentBoundaryWarning(row);
    const districtId = comparesParent
      ? row.parentBoundaryComparison?.parentDistrictId
      : row.match?.districtId;
    const requestId = comparisonMapRequestRef.current + 1;
    comparisonMapRequestRef.current = requestId;
    setComparisonMap({ row });
    setComparisonMapLoading(true);
    setComparisonMapError(null);

    if (!districtId) {
      setComparisonMapError('The matched district is unavailable.');
      setComparisonMapLoading(false);
      return;
    }

    try {
      let countyId = comparesParent ? undefined : job?.county;
      if (!comparesParent && !countyId) {
        const contestShapefile = await feathers.getService('contest-shapefiles').get(
          job?._id || id,
          {
            query: {
              $includeRows: false,
              $select: ['_id', 'county']
            }
          }
        );
        countyId = contestShapefile.county;
        if (countyId) {
          setJob(current => current ? { ...current, county: countyId! } : current);
        }
      }
      if (!comparesParent && !countyId) {
        throw new Error('The contest shapefile county is unavailable.');
      }

      const [incomingBoundary, district, county] = await Promise.all([
        loadRowGeometry(row.dataIndex),
        feathers.getService('districts').get(districtId, {
          query: { $includeGeoDistrict: true }
        }),
        countyId
          ? feathers.getService('districts').get(countyId, {
            query: { $includeGeoDistrict: true }
          })
          : Promise.resolve(null)
      ]);
      if (!incomingBoundary) {
        throw new Error('The incoming boundary is unavailable.');
      }
      if (!district.geoDistrict?.boundary) {
        throw new Error(
          comparesParent
            ? 'The parent district boundary is unavailable.'
            : 'The existing district boundary is unavailable.'
        );
      }
      if (county && !county.geoDistrict?.boundary) {
        throw new Error('The county boundary is unavailable.');
      }
      if (comparisonMapRequestRef.current !== requestId) return;
      setComparisonMap({
        row,
        incomingBoundary,
        existingBoundary: district.geoDistrict.boundary,
        countyBoundary: county?.geoDistrict?.boundary,
        countyName: county?.longName,
        comparisonBoundaryName: district.longName
          || district.name
          || district.matchName
      });
    } catch (requestError) {
      if (comparisonMapRequestRef.current !== requestId) return;
      setComparisonMapError(errorMessage(requestError));
    } finally {
      if (comparisonMapRequestRef.current === requestId) {
        setComparisonMapLoading(false);
      }
    }
  };

  const existingBoundaryIndicator = (row: ContestShapefileRow) => {
    const comparison = row.boundaryComparison;
    const appendsBoundary = isAppendBoundaryRow(row);
    const parentBoundaryWarning = hasParentBoundaryWarning(row);
    const needsAttention = comparison?.significantDifference === true;
    const differencePercent = comparison
      ? Math.round(comparison.boundaryDifferenceRatio * 100)
      : null;
    const containedPercent = row.parentBoundaryComparison
      ? Math.round(row.parentBoundaryComparison.containedRatio * 100)
      : null;

    const indicator = (
      <LockedMapIndicator
        aria-hidden
        $needsAttention={needsAttention || parentBoundaryWarning}
        $appendsBoundary={appendsBoundary && !parentBoundaryWarning}
        $parentBoundaryWarning={parentBoundaryWarning}
      >
        <MapOutlinedIcon />
        {parentBoundaryWarning
          ? <WarningAmberIcon />
          : appendsBoundary
          ? <AddIcon />
          : needsAttention
            ? <WarningAmberIcon />
            : <LockIcon />}
      </LockedMapIndicator>
    );

    return row.included && (
      row.match?.district?.hasGeodistrict
      || appendsBoundary
      || parentBoundaryWarning
    ) ? (
      <Tooltip
        title={parentBoundaryWarning
          ? `Only ${containedPercent}% of the new district boundary lies within its parent district.`
          : appendsBoundary
          ? 'The incoming boundary will be appended to this district’s partial boundary.'
          : needsAttention
            ? `The suggested boundary differs from the existing district boundary by ${differencePercent}%. A new boundary will not be created.`
            : 'This district already has a geodistrict. A new boundary will not be created.'}
        arrow
      >
        {parentBoundaryWarning || needsAttention || appendsBoundary ? (
          <BoundaryComparisonButton
            type="button"
            aria-label={parentBoundaryWarning
              ? 'Compare parent and new district boundaries'
              : appendsBoundary
              ? 'View partial and incoming district boundaries'
              : 'Compare existing and incoming district boundaries'}
            onClick={() => openComparisonMap(row)}
          >
            {indicator}
          </BoundaryComparisonButton>
        ) : (
          <span aria-label="District already has a geodistrict">
            {indicator}
          </span>
        )}
      </Tooltip>
    ) : null
  };

  const upload = async (file: File) => {
    if (!selectedCounty) {
      setError('Select a county before uploading a GeoPackage.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.gpkg')) {
      setError('Contest shapefiles must be uploaded as a .gpkg file.');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const { accessToken } = await feathers.get('authentication');
      const baseUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:3030'
        : 'https://www.branch.vote';
      const uploadResult = (
        await superagent
          .post(`${baseUrl}/api/v1/uploads`)
          .set('Authorization', accessToken)
          .query({ type: 'contestShapefiles' })
          // superagent's Node typings do not include the browser File overload.
          .attach('file', file as any)
      ).body;
      const created = await feathers.getService('contest-shapefiles').create({
        election: electionKey,
        county: selectedCounty._id,
        key: uploadResult.key,
        bucket: uploadResult.bucket,
        ...(uploadResult.location ? { location: uploadResult.location } : {})
      });
      history.replace(`/elections/${electionKey}/contest-shapefiles/${created._id}`);
      setJob(created);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const runAction = async (
    patch: PatchContestShapefile
  ) => {
    if (!job) return null;
    setMutating(true);
    setError(null);
    try {
      const updated = await feathers.getService('contest-shapefiles').patch(job._id, patch);
      setJob(updated);
      await loadDataOperations();
      return updated;
    } catch (requestError) {
      setError(errorMessage(requestError));
      return null;
    } finally {
      setMutating(false);
    }
  };

  const saveDataOperation = async (updatedOperation: DataOperation) => {
    const storedOperation = updatedOperation as StoredDataOperation;
    setError(null);
    try {
      const savedOperation = await feathers.getService('data-operations').patch(
        storedOperation._id,
        { data: storedOperation.data }
      );
      setDataOperations(current => current.map(operation => (
        operation._id === savedOperation._id ? savedOperation : operation
      )));
    } catch (requestError) {
      setError(errorMessage(requestError));
      await loadDataOperations();
    }
  };

  const confirmCreation = async () => {
    if (!validation.canCreate) return;
    const updated = await runAction({ $createGeoDistricts: true });
    if (!updated) return;
    setCreationDialogOpen(false);
    history.push(`/elections/${electionKey}/contest-shapefiles/${job?._id}/created`);
  };

  const deleteContestShapefile = async () => {
    if (!job) return;
    setMutating(true);
    setError(null);
    try {
      await feathers.getService('contest-shapefiles').remove(job._id);
      history.push(`/elections/${electionKey}/contest-shapefiles`);
    } catch (requestError) {
      setError(errorMessage(requestError));
      setDeleteDialogOpen(false);
    } finally {
      setMutating(false);
    }
  };

  const lastAutomatedStatus = [...(job?.statusEntries || [])]
    .reverse()
    .find(entry => ['processing', 'labeling', 'creating'].includes(entry.status))?.status;
  const canRetryMatching = Boolean(
    job
    && job.processedRows > 0
    && (
      job.status === 'needs-review'
      || (['error', 'canceled'].includes(job.status) && lastAutomatedStatus === 'labeling')
    )
  );
  const shapefileS3Link = job?.location
    || (job?.bucket && job?.key ? `s3://${job.bucket}/${job.key}` : null);
  const copyShapefileS3Link = async () => {
    if (!shapefileS3Link) return;
    try {
      await navigator.clipboard.writeText(shapefileS3Link);
      setS3LinkCopied(true);
    } catch (copyError) {
      setError(`Could not copy the shapefile S3 link: ${errorMessage(copyError)}`);
    }
  };

  const workflowCurrentStep = job?.status === 'processing'
    ? 0
    : job?.status === 'labeling'
      ? 1
      : showCreatedStep
        ? 3
        : 2;
  const countyName = county?.name
    || county?.longName
    || county?.shortName
    || county?.matchName
    || 'County';
  const navigateToWorkflowStep = (nextStep: 'verified' | 'created') => {
    if (!job) return;
    history.push(`/elections/${electionKey}/contest-shapefiles/${job._id}/${nextStep}`);
  };

  const header = (
    <DataDetailToolbar
      onBack={{ to: { pathname: `/elections/${electionKey}/contest-shapefiles` } }}
      navTree={[
        { text: 'Elections', to: { pathname: '/elections' } },
        { text: election?.name || 'Election', to: { pathname: `/elections/${electionKey}` } },
        { text: 'Contest Shapefiles', to: { pathname: `/elections/${electionKey}/contest-shapefiles` } },
        { text: mode === 'new' ? 'New Upload' : countyName }
      ]}
      actionButtonsComponent={mode === 'existing' ? (
        <Tooltip title="View activity">
          <span>
            <IconButton
              aria-label="View activity"
              disabled={!job?.statusEntries?.length}
              onClick={() => setActivityOpen(true)}
            >
              <HistoryIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      navigationTrailingComponent={mode === 'existing' ? (
        <StepTracker
          compact
          currentStep={workflowCurrentStep}
          steps={['Processed', 'Matched', 'Verified', 'Created'].map((name, index) => ({
            name,
            isComplete: job?.status === 'completed'
              ? true
              : index < workflowCurrentStep,
            isInProgress: index === workflowCurrentStep
              && ['processing', 'labeling', 'creating'].includes(job?.status || ''),
            disabled: index < 2 || (index === 2
              ? !job || !['needs-review', 'creating', 'completed', 'canceled'].includes(job.status)
              : !creationHasStarted),
            onClick: index === 2
              ? () => navigateToWorkflowStep('verified')
              : index === 3 && creationHasStarted
                ? () => navigateToWorkflowStep('created')
                : undefined
          }))}
        />
      ) : null}
      MoreButton={mode === 'existing' ? [
        ...(canRetryMatching ? [{
          text: 'Retry matching',
          disabled: mutating,
          onClick: () => runAction({ $retry: { stage: 'labeling' } })
        }] : []),
        {
          text: 'Delete contest shapefile',
          icon: <DeleteOutlineIcon fontSize="small" />,
          disabled: mutating || !job,
          onClick: () => setDeleteDialogOpen(true)
        }
      ] : undefined}
    />
  );

  if (mode === 'new') {
    return (
      <Wrapper>
        {header}
        {error && <Alert severity="error">{error}</Alert>}
        <Section>
          <Typography variant="h6">Upload contest shapefile</Typography>
          <Typography variant="body2" color="textSecondary">
            Select the county represented by the GeoPackage, then upload one .gpkg file.
          </Typography>
          <Field>
            <Typography variant="subtitle2">County</Typography>
            <DistrictSelectInput
              value={selectedCounty}
              descendantOf={election?.state || null}
              districtTypes={['county']}
              onChange={(event: { target: { value: District | null } }) => setSelectedCounty(event.target.value)}
              style={{ width: '100%' }}
              placeholder="Select county"
            />
          </Field>
          <input
            ref={fileInput}
            type="file"
            accept=".gpkg"
            hidden
            onChange={event => {
              const file = event.target.files?.[0];
              if (file) upload(file);
            }}
          />
          <Button
            variant="contained"
            disabled={!selectedCounty || uploading}
            onClick={() => fileInput.current?.click()}
          >
            {uploading ? 'Uploading...' : 'Choose .gpkg File'}
          </Button>
        </Section>
      </Wrapper>
    );
  }

  if (loading && !job) {
    return <Wrapper>{header}<Centered><LoadingSpinner width={64} /></Centered></Wrapper>;
  }

  if (!job) {
    return <Wrapper>{header}<Alert severity="error">{error || 'Contest shapefile not found.'}</Alert></Wrapper>;
  }

  const jobError = getContestShapefileErrorMessage(job);
  const operationProgress = dataOperations.reduce((counts, operation) => ({
    completed: counts.completed + Number(operation.status === 'completed'),
    failed: counts.failed + Number(operation.status === 'failed')
  }), { completed: 0, failed: 0 });
  const processingTotal = job.status === 'creating'
    ? dataOperations.length || job.creationTotal
    : job.totalRows;
  const processingValue = job.status === 'creating'
    ? operationProgress.completed + operationProgress.failed
    : job.status === 'labeling'
      ? job.matchedRows
      : job.processedRows;
  const progress = processingTotal > 0
    ? Math.min(100, Math.round((processingValue / processingTotal) * 100))
    : 0;
  const isActive = isContestShapefileActive(job.status);
  const processStage = job.processInProgress?.stage;
  const activeProcessStage = processStage && processStage !== 'none'
    ? processStage
    : isActive
      ? job.status
      : null;
  const activeProcessTitle = activeProcessStage === 'processing'
    ? 'Processing uploaded package'
    : activeProcessStage === 'labeling'
      ? 'Matching contests'
      : activeProcessStage === 'creating'
        ? 'Creating geodistricts'
        : null;
  const hasRetryableCreationOperations = Boolean(
    !isActive
    && dataOperations.some(operation => ['failed', 'not-started'].includes(operation.status))
  );
  const isRecoverable = ['error', 'canceled'].includes(job.status);
  const terminalOperation = lastAutomatedStatus === 'labeling'
    ? 'Contest matching'
    : lastAutomatedStatus === 'creating'
      ? 'Geodistrict creation'
      : 'Package processing';
  const statusTitle = job.status === 'processing'
    ? 'Processing uploaded package'
    : job.status === 'labeling'
      ? 'Matching contests'
      : job.status === 'creating'
        ? 'Creating geodistricts'
        : job.status === 'needs-review'
          ? 'Geodistrict creation paused'
          : job.status === 'completed'
            ? 'Geodistrict creation complete'
            : job.status === 'error'
              ? `${terminalOperation} failed`
              : `${terminalOperation} canceled`;
  const showStatusCard = job.status === 'canceled' && lastAutomatedStatus === 'creating'
    ? false
    : job.status !== 'needs-review'
      ? statusTitle !== 'Package processing canceled' || Boolean(jobError)
      : Boolean(jobError);
  const statusDescription = job.status === 'processing'
    ? `${processingValue} of ${processingTotal || '?'} rows processed`
    : job.status === 'labeling'
      ? `${processingValue} of ${processingTotal || '?'} contests matched`
      : job.status === 'creating'
        ? `${processingValue} of ${processingTotal || '?'} geodistricts created`
        : job.status === 'needs-review'
          ? 'Review the error and retry creation when ready.'
          : job.status === 'completed'
            ? `${job.creationSucceeded} geodistricts created${job.creationFailed ? ` · ${job.creationFailed} failed` : ''}`
            : 'Retry continues from the interrupted stage without repeating completed work.';

  return (
    <Wrapper>
      {header}
      {error && <Alert severity="error">{error}</Alert>}
      {activeProcessTitle && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} color="inherit" />}
        >
          {activeProcessTitle}. This page will update automatically.
        </Alert>
      )}
      {showStatusCard && (
        <Section>
          <ProgressHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isActive && <CircularProgress size={22} />}
              <div>
                <Typography variant="h6">{statusTitle}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {statusDescription}
                </Typography>
              </div>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isRecoverable && lastAutomatedStatus === 'processing' && (
                <Button
                  variant="contained"
                  disabled={mutating}
                  onClick={() => runAction({ $retry: true })}
                >
                  Retry Processing
                </Button>
              )}
              {isActive && (
                <Button
                  color="error"
                  variant="outlined"
                  disabled={mutating}
                  onClick={() => runAction({ $cancel: true })}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </ProgressHeader>
          {jobError && <Alert severity="error">{jobError}</Alert>}
          {isActive && (
            <LinearProgress
              variant={processingTotal > 0 ? 'determinate' : 'indeterminate'}
              value={processingTotal > 0 ? progress : undefined}
            />
          )}
        </Section>
      )}

      {showVerifiedStep && (rows.length > 0 || job.status === 'needs-review') && (
        <Section>
          <ProgressHeader>
            <Typography variant="h6">Contests</Typography>
          </ProgressHeader>
          {showVerifiedStep && (
            <ReviewProgressSection>
              <ReviewProgressInfo>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Review Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {reviewProgress.completed} of {reviewProgress.total} ready
                  </Typography>
                  <TextField
                    inputRef={reviewSearchInput}
                    size="small"
                    value={reviewSearch}
                    placeholder="Search contests or districts"
                    aria-label="Search contests or assigned districts"
                    onChange={(event) => {
                      setReviewSearch(event.target.value);
                      changeRowPage(1);
                    }}
                    sx={{ width: 280, backgroundColor: 'common.white' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button
                    size="small"
                    variant={attentionFilter ? 'contained' : 'outlined'}
                    startIcon={<FilterListIcon />}
                    aria-pressed={Boolean(attentionFilter)}
                    onClick={() => {
                      setAttentionFilter(current => current ? null : true);
                      changeRowPage(1);
                    }}
                    sx={{
                      borderRadius: '9999px',
                      borderColor: attentionFilter ? 'primary.main' : 'divider',
                      backgroundColor: attentionFilter ? 'primary.main' : 'common.white',
                      color: attentionFilter ? 'primary.contrastText' : 'text.primary',
                      boxShadow: 'none',
                      textTransform: 'none',
                      fontWeight: 400,
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: attentionFilter ? 'primary.dark' : 'primary.light',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    Needs attention ({attentionRows.length})
                  </Button>
                  <Tooltip title={s3LinkCopied ? 'Copied!' : 'Copy shapefile S3 link'}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Copy shapefile S3 link"
                        disabled={!shapefileS3Link}
                        onClick={copyShapefileS3Link}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </ReviewProgressInfo>
              <ProgressBar
                percentage={reviewProgress.percentage}
                height={8}
                backgroundColor="#E0E0E0"
                color="#4CAF50"
              />
            </ReviewProgressSection>
          )}
          {loading ? <Centered><LoadingSpinner width={48} /></Centered> : (
            <ReviewTable>
              <ReviewHeader>
                <span />
                <Typography variant="subtitle2">Contest</Typography>
                <Typography variant="subtitle2">District match</Typography>
                <span />
              </ReviewHeader>
              {visibleRows.map(row => {
                const saving = savingRows.has(row.dataIndex);
                const attentionMessage = isActive
                  ? null
                  : contestShapefileRowAttentionMessage(row);
                const districtId = row.match?.districtId;
                const createWithData = row.match?.createWithData;
                const suggestedParentId = contestShapefileRowParentDistrictId(
                  row,
                  election?.state
                );
                const suggestedDistrict = createWithData ? {
                  number: createWithData.number,
                  name: createWithData.name,
                  longName: createWithData.longName,
                  letter: createWithData.letter,
                  type: createWithData.type,
                  parentId: createWithData.parentId,
                  parentDistrict: typeof createWithData.parentDistrict === 'string'
                    ? createWithData.parentDistrict
                    : createWithData.parentDistrict?.name
                } : undefined;
                const creationSourceType = suggestedDistrict?.type || row.match?.district?.type;
                return (
                  <ReviewRow key={row.dataIndex} $excluded={!row.included}>
                    <div>
                      {attentionMessage && (
                        <Tooltip title={attentionMessage} arrow>
                          <WarningAmberIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                    </div>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.contest}</Typography>
                    <DistrictMatchContent>
                      <DistrictCell
                        district={row.match?.district || null}
                        suggestCreationAs={suggestedDistrict}
                        state={election?.state || ''}
                        stateConfiguration={stateConfiguration}
                        confidence={row.match?.confidence ?? undefined}
                        saving={saving}
                        disableEditing={!row.included}
                        readOnly={isActive || creationHasStarted}
                        suggestedGeometry={rowGeometries[row.dataIndex]}
                        loadSuggestedGeometry={() => loadRowGeometry(row.dataIndex)}
                        suggestedParentDistrict={suggestedParentId
                          ? parentDistricts[suggestedParentId]
                          : undefined}
                        loadSuggestedParentDistrict={suggestedParentId
                          ? () => loadParentDistrict(suggestedParentId)
                          : undefined}
                        creationSources={shapefileS3Link && creationSourceType
                          ? [{
                            url: shapefileS3Link,
                            type: creationSourceType
                          }]
                          : undefined}
                        creationNotes={[
                          'This boundary was determined using Contest-specific shapefiles'
                        ]}
                        allowChildCreation
                        onDistrictChange={(district: District) => patchRow(
                          row,
                          {
                            $updateRowMatch: { dataIndex: row.dataIndex, districtId: district._id }
                          },
                          assignContestShapefileRowDistrict(row, district)
                        )}
                        onDistrictClear={() => patchRow(
                          row,
                          {
                            $updateRowMatch: { dataIndex: row.dataIndex, districtId: null }
                          },
                          clearContestShapefileRowDistrict(row)
                        )}
                        onChangeConfidence={() => {
                          if (districtId) {
                            patchRow(
                              row,
                              {
                                $updateRowMatch: {
                                  dataIndex: row.dataIndex,
                                  districtId
                                }
                              },
                              confirmContestShapefileRowMatch(row)
                            );
                          }
                        }}
                      />
                      {existingBoundaryIndicator(row)}
                    </DistrictMatchContent>
                    <RowActions>
                      {mapButton(row)}
                      <Tooltip title={row.included ? 'Delete contest' : 'Restore contest'}>
                        <span>
                          <IconButton
                            size="small"
                            color={row.included ? 'error' : 'default'}
                            disabled={saving || isActive || creationHasStarted}
                            aria-label={row.included ? `Delete ${row.contest}` : `Restore ${row.contest}`}
                            onClick={() => patchRow(
                              row,
                              {
                                $setRowIncluded: {
                                  dataIndex: row.dataIndex,
                                  included: !row.included
                                }
                              },
                              setContestShapefileRowIncluded(row, !row.included)
                            )}
                          >
                            {row.included
                              ? <DeleteOutlineIcon fontSize="small" />
                              : <RestoreFromTrashIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </RowActions>
                  </ReviewRow>
                );
              })}
            </ReviewTable>
          )}
          {visibleRowTotal > ROW_PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
              <Pagination
                page={rowPage}
                count={Math.ceil(visibleRowTotal / ROW_PAGE_SIZE)}
                onChange={(_, page) => changeRowPage(page)}
                color="primary"
              />
            </Box>
          )}
          {!creationHasStarted && job.status === 'needs-review' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                disabled={!validation.canCreate || mutating || loading}
                onClick={() => setCreationDialogOpen(true)}
              >
                Create
              </Button>
            </Box>
          )}
        </Section>
      )}

      {showCreatedStep && (
        <DataOperationsTable
          dataOperations={dataOperations as DataOperation[]}
          title="Geodistrict Operations"
          editingDisabled
          editableJson={!isActive}
          operationsOnly
          onChangeDataOperation={saveDataOperation}
          onRetryFailedOperations={hasRetryableCreationOperations
            ? () => runAction({ $retry: { stage: 'creating' } })
            : undefined}
        />
      )}

      <Dialog
        open={creationDialogOpen}
        onClose={() => {
          if (!mutating) setCreationDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create geodistricts?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {validation.eligibleCreationCount} geodistrict
            {validation.eligibleCreationCount === 1 ? '' : 's'} will be updated.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ marginTop: 1 }}>
            Deleted rows and districts that already have a geodistrict will be ignored.
            Matches cannot be edited after this starts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={mutating} onClick={() => setCreationDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={mutating} onClick={confirmCreation}>
            {mutating ? 'Starting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!mutating) setDeleteDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete contest shapefile?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            This will permanently delete this contest shapefile entry. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={mutating} onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={mutating}
            onClick={deleteContestShapefile}
          >
            {mutating ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(mapRow)}
        onClose={() => setMapRow(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{mapRow?.contest || 'Contest boundary'}</DialogTitle>
        <DialogContent dividers>
          <MapContainer>
            {mapLoading ? (
              <Centered><LoadingSpinner width={48} /></Centered>
            ) : mapError ? (
              <Alert severity="error">{mapError}</Alert>
            ) : mapRow?.geometry ? (
              <InteractiveMap
                districts={undefined}
                features={[formatGeoJSON(mapRow.geometry, {
                  fillColor: '#4F50CC',
                  strokeColor: '#4F50CC',
                  tooltip: mapRow.contest
                })]}
              />
            ) : null}
          </MapContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(comparisonMap)}
        onClose={closeComparisonMap}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {hasParentBoundaryWarning(comparisonMap?.row)
            ? 'Check parent boundary'
            : isAppendBoundaryRow(comparisonMap?.row)
            ? 'Append boundary'
            : 'Compare boundaries'} · {comparisonMap?.row.contest || 'Contest'}
        </DialogTitle>
        <DialogContent dividers>
          <div>
            <>
              <MapContainer>
                {comparisonMapLoading ? (
                  <Centered><LoadingSpinner width={48} /></Centered>
                ) : comparisonMapError ? (
                  <Alert severity="error">{comparisonMapError}</Alert>
                ) : comparisonMap?.incomingBoundary && comparisonMap.existingBoundary ? (
                  <InteractiveMap
                    districts={undefined}
                    features={[
                    ...(comparisonMap.countyBoundary ? [
                      formatGeoJSON(comparisonMap.countyBoundary, {
                        fillColor: '#DCE6F2',
                        strokeColor: '#90A4AE',
                        tooltip: comparisonMap.countyName || 'County boundary',
                        excludeFromBounds: true
                      })
                    ] : []),
                      formatGeoJSON(comparisonMap.existingBoundary, {
                        fillColor: '#ED6C02',
                        strokeColor: '#ED6C02',
                        tooltip: hasParentBoundaryWarning(comparisonMap.row)
                          ? comparisonMap.comparisonBoundaryName || 'Parent district boundary'
                          : 'Existing boundary'
                      }),
                      formatGeoJSON(comparisonMap.incomingBoundary, {
                        fillColor: isAppendBoundaryRow(comparisonMap.row) ? '#2E7D32' : '#4F50CC',
                        strokeColor: isAppendBoundaryRow(comparisonMap.row) ? '#2E7D32' : '#4F50CC',
                        tooltip: hasParentBoundaryWarning(comparisonMap.row)
                          ? 'New district boundary'
                          : isAppendBoundaryRow(comparisonMap.row)
                          ? 'New boundary to append'
                          : 'Incoming boundary'
                      })
                    ]}
                  />
                ) : null}
              </MapContainer>
              {!comparisonMapLoading
                && !comparisonMapError
                && comparisonMap?.incomingBoundary
                && comparisonMap.existingBoundary && (
                <BoundaryLegend aria-label="Boundary map legend">
                  <>
                    {comparisonMap.countyBoundary && (
                      <LegendItem>
                        <LegendSwatch $color="#DCE6F2" $borderColor="#90A4AE" />
                        <Typography variant="body2">County boundary</Typography>
                      </LegendItem>
                    )}
                    <LegendItem>
                      <LegendSwatch $color="#ED6C02" />
                      <Typography variant="body2">
                        {hasParentBoundaryWarning(comparisonMap.row)
                          ? comparisonMap.comparisonBoundaryName || 'Parent district boundary'
                          : 'Existing boundary'}
                      </Typography>
                    </LegendItem>
                    <LegendItem>
                      <LegendSwatch
                        $color={isAppendBoundaryRow(comparisonMap.row) ? '#2E7D32' : '#4F50CC'}
                      />
                      <Typography variant="body2">
                        {hasParentBoundaryWarning(comparisonMap.row)
                          ? 'New district boundary'
                          : isAppendBoundaryRow(comparisonMap.row)
                          ? 'New boundary to append'
                          : 'Incoming boundary'}
                      </Typography>
                    </LegendItem>
                  </>
                </BoundaryLegend>
              )}
            </>
          </div>
        </DialogContent>
        <DialogActions>
          {hasParentBoundaryWarning(comparisonMap?.row) && !creationHasStarted && (
            <Button
              color="warning"
              variant="contained"
              disabled={Boolean(
                comparisonMap?.row
                && savingRows.has(comparisonMap.row.dataIndex)
              )}
              onClick={approveParentBoundaryWarning}
            >
              Approve without changes
            </Button>
          )}
          <Button onClick={closeComparisonMap}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Activity</DialogTitle>
        <DialogContent dividers>
          <ActivityList>
            {(job.statusEntries || []).slice().reverse().map((entry, index) => (
              <ActivityRow key={`${entry.enteredAt || ''}-${index}`}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                  {entry.status.replace('-', ' ')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {entry.enteredAt ? new Date(entry.enteredAt).toLocaleString() : 'Time unavailable'}
                </Typography>
              </ActivityRow>
            ))}
          </ActivityList>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: calc(100% - 48px);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.div`
  padding: 20px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  width: 420px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const ReviewProgressSection = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 16px 24px;
  background: #f7f9fe;
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ReviewProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActivityRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 12px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: 0;
  }
`;

const ReviewTable = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
`;

const ReviewHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 2fr 2fr 88px;
  gap: 16px;
  align-items: center;
  padding: 12px 16px;
  background: #f7f9fe;
`;

const ReviewRow = styled.div<{ $excluded: boolean }>`
  display: grid;
  grid-template-columns: 40px 2fr 2fr 88px;
  gap: 16px;
  align-items: center;
  padding: ${props => props.$excluded ? '6px 16px' : '12px 16px'};
  border-top: 1px solid #eee;
  background: ${props => props.$excluded ? '#f5f5f5' : '#fff'};
  opacity: ${props => props.$excluded ? 0.6 : 1};
  transition: padding 0.2s ease, background-color 0.2s ease, opacity 0.2s ease;

  ${props => props.$excluded && `
    .MuiTypography-root {
      font-size: 0.75rem;
    }
  `}
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const DistrictMatchContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const BoundaryComparisonButton = styled.button`
  display: inline-flex;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid #1976d2;
    outline-offset: 2px;
  }
`;

const LockedMapIndicator = styled.span<{
  $needsAttention: boolean;
  $appendsBoundary: boolean;
  $parentBoundaryWarning: boolean;
}>`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  color: ${props => props.$parentBoundaryWarning
    ? '#d32f2f'
    : props.$appendsBoundary
      ? '#81c784'
      : props.$needsAttention
        ? '#ed6c02'
        : '#9e9e9e'};

  > svg:first-child {
    font-size: 24px;
  }

  > svg:last-child {
    position: absolute;
    right: 0;
    bottom: 0;
    padding: 1px;
    border-radius: 50%;
    background: #fff;
    font-size: 13px;
    color: ${props => props.$parentBoundaryWarning
      ? '#d32f2f'
      : props.$appendsBoundary
        ? '#2e7d32'
        : props.$needsAttention
          ? '#ed6c02'
          : '#757575'};
  }
`;

const MapContainer = styled.div`
  height: 520px;
  width: 100%;
`;

const BoundaryLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  padding-top: 16px;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const LegendSwatch = styled.span<{ $color: string; $borderColor?: string }>`
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.$borderColor || props.$color};
  border-radius: 3px;
  background: ${props => props.$color}80;
`;

export default ContestShapefileDetail;
